import React, { Component, Fragment } from "react";
import { Switch, Route, withRouter, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { set_user } from "../store/actions/utils/user";
import {
  getUser,
  authContext,
  shouldAttemptInteractiveLogin,
  clearInteractiveLoginMarker,
} from "../services/auth";
import {
  db_get_email_consultant,
  db_get_consultants,
} from "../store/actions/tables/consultants";

import Homepage from "./Homepage";
import ContractData from "./Contract/ContractData";
import AmendedData from "./Amended/AmendedData";
import Loading from "../components/Graphics/Loading";
import AuthError from "../components/Security/AuthError";
import ServerDown from "../components/Security/ServerDown";

// Long enough that a real redirect always wins the race, short enough that a
// redirect which never navigates does not strand the user on a spinner.
const AUTH_REDIRECT_GRACE_MS = 4000;

export class Home extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      authError: false,
      serverDown: false,
    };
  }

  componentWillUnmount() {
    clearTimeout(this.authErrorTimer);
  }

  async componentDidMount() {
    getUser()
      .then((auth_user) => {
        db_get_email_consultant(auth_user)
          .then(async (consultant) => {
            localStorage.setItem("apiToken", consultant.token);
            await this.props.set_user({
              ...auth_user,
              role: consultant.role,
              id: consultant.result.time_tracker_id,
              sas: consultant.result.SAS_TOKEN,
            });
            await this.props.db_get_consultants();
            clearInteractiveLoginMarker();
            this.setState({ ...this.state, loaded: true });
          })
          .catch(() => {
            this.setState({ ...this.state, loaded: true, serverDown: true });
          });
      })
      .catch(() => {
        // Silent renewal is already spent by the time we get here (Safari's
        // tracking protection, Incognito, privacy extensions all block the
        // iframe). One interactive redirect fixes that first-party; the
        // session flag stops a redirect loop when it doesn't.
        const attempt = shouldAttemptInteractiveLogin();
        if (!attempt) {
          this.setState({ ...this.state, loaded: true, authError: true });
          return;
        }
        // A redirect navigates away in well under a second, so keep the
        // loader up while it does. Painting AuthError first told anyone whose
        // token had simply lapsed that their session was broken, moments
        // before signing them straight in. The timer preserves what painting
        // first was protecting: a login() that returns without navigating
        // still lands on the error page instead of a dead loader.
        this.authErrorTimer = setTimeout(() => {
          this.setState({ ...this.state, loaded: true, authError: true });
        }, AUTH_REDIRECT_GRACE_MS);
        try {
          authContext.login();
        } catch {
          clearTimeout(this.authErrorTimer);
          this.setState({ ...this.state, loaded: true, authError: true });
        }
      });
  }

  render() {
    return (
      <Fragment>
        {!this.state.loaded && <Loading />}
        {this.state.authError && <AuthError />}
        {this.state.serverDown && <ServerDown />}
        {this.state.loaded &&
          !this.state.authError &&
          !this.state.serverDown && (
            <Switch>
              <Route
                exact
                path="/"
                render={(props) => <Redirect to="/dashboard" />}
              />
              <Route
                path="/dashboard"
                render={({ props, match }) => (
                  <Homepage user={this.props.user} match={match} {...props} />
                )}
              />
              <Route
                exact
                path="/assignments/admin/:id"
                render={(props) => (
                  <ContractData
                    role="Admin"
                    user={this.props.user}
                    {...props}
                  />
                )}
              />
              <Route
                exact
                path="/assignments/am/:id"
                render={(props) => (
                  <ContractData
                    role="Client Manager"
                    user={this.props.user}
                    {...props}
                  />
                )}
              />
              <Route
                exact
                path="/amendment/admin/:id"
                render={(props) => (
                  <AmendedData role="Admin" user={this.props.user} {...props} />
                )}
              />
              <Route
                exact
                path="/amendment/am/:id"
                render={(props) => (
                  <AmendedData
                    role="Client Manager"
                    user={this.props.user}
                    {...props}
                  />
                )}
              />
              <Route
                path="/*"
                render={(props) => <Redirect to="/dashboard" />}
              />
            </Switch>
          )}
      </Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.user,
  };
}

export default connect(mapStateToProps, { set_user, db_get_consultants })(
  withRouter(Home)
);
